import axiosInstance from '@/api/apiClient'
import { getErrorMessage } from '@/lib/utils'

export type ImportScope =
  | 'full_game'
  | 'advisors'
  | 'players_teams'
  | 'ai_players'
  | 'scenario_events'
  | 'rounds_timers'
  | 'communications'
  | 'resource_limits'
  | 'world_setup'
  | 'decisions'

export interface PreviewItem {
  id: string
  label: string
  is_duplicate: boolean
  parent_id?: string | null
  phase_type?: string | null
  sender_email?: string | null
  hint?: string
  flags?: string[]
  severity?: 'info' | 'warn'
}

export interface SectionPreview {
  count: number
  duplicate_count: number
  items: PreviewItem[]
}

export interface ImportPreviewResponse {
  export_version: string
  scope: ImportScope | string
  mcp_servers_only?: boolean
  sections: Record<string, SectionPreview>
}

export interface ImportSelections {
  scope?: ImportScope | string
  mcp_servers_only?: boolean
  selected_sections?: string[]
  replace_rounds?: boolean
  included_item_ids?: string[]
  provision_missing_student_accounts?: boolean
  require_password_configuration?: boolean
}

export interface ImportWarning {
  code: string
  message: string
  detail?: unknown
}

export interface ImportResultBase {
  import_warnings?: ImportWarning[]
}

export function getImportWarnings(
  data: Record<string, unknown> | ImportResultBase
): ImportWarning[] {
  const warnings = data.import_warnings
  if (!Array.isArray(warnings)) return []
  return warnings.filter(
    (w): w is ImportWarning =>
      typeof w === 'object' &&
      w !== null &&
      typeof (w as ImportWarning).code === 'string' &&
      typeof (w as ImportWarning).message === 'string'
  )
}

function buildValidateUrl(scope: ImportScope, roomId?: string): string {
  const q = new URLSearchParams({ scope }).toString()
  if (roomId) {
    return `/oge/rooms/${roomId}/import/validate/?${q}`
  }
  return `/oge/rooms/import/validate/?${q}`
}

function buildImportUrl(scope: ImportScope, roomId: string | undefined): string {
  if (scope === 'full_game' || !roomId) {
    return `/oge/rooms/import/`
  }
  const paths: Record<Exclude<ImportScope, 'full_game'>, string> = {
    advisors: 'advisors',
    players_teams: 'players-teams',
    ai_players: 'ai-players',
    scenario_events: 'scenario-events',
    rounds_timers: 'rounds-timers',
    communications: 'communications',
    resource_limits: 'resource-limits',
    world_setup: 'world-setup',
    decisions: 'decisions',
  }
  const seg = paths[scope as Exclude<ImportScope, 'full_game'>]
  return `/oge/rooms/${roomId}/import/${seg}/`
}

export async function validateImportAPI(
  file: File,
  scope: ImportScope,
  roomId?: string,
  options?: { mcpServersOnly?: boolean; provisionMissingStudentAccounts?: boolean }
): Promise<ImportPreviewResponse> {
  const fd = new FormData()
  fd.append('file', file)
  const selections: ImportSelections = {
    scope,
    mcp_servers_only: options?.mcpServersOnly,
    ...(options?.provisionMissingStudentAccounts
      ? { provision_missing_student_accounts: true }
      : {}),
  }
  fd.append('selections', JSON.stringify(selections))
  try {
    const res = await axiosInstance.post<ImportPreviewResponse>(
      buildValidateUrl(scope, roomId),
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return res.data
  } catch (e) {
    throw new Error(getErrorMessage(e))
  }
}

export async function importGameAPI(
  file: File,
  selections: ImportSelections
): Promise<{ room_id: string; imported_sections: string[] } & ImportResultBase> {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('selections', JSON.stringify(selections))
  try {
    const res = await axiosInstance.post(`/oge/rooms/import/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return res.data
  } catch (e) {
    throw new Error(getErrorMessage(e))
  }
}

export async function importSectionAPI(
  roomId: string,
  scope: ImportScope,
  file: File,
  selections: ImportSelections,
  options?: { mcpServersOnly?: boolean }
): Promise<Record<string, unknown>> {
  const fd = new FormData()
  fd.append('file', file)
  fd.append(
    'selections',
    JSON.stringify({
      ...selections,
      scope,
      mcp_servers_only: options?.mcpServersOnly ?? selections.mcp_servers_only,
    })
  )
  const url = buildImportUrl(scope, roomId)
  try {
    const res = await axiosInstance.post(url, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return res.data
  } catch (e) {
    throw new Error(getErrorMessage(e))
  }
}
